/**
 * <s-collar-configurator> — wiring layer for "Crea su collar".
 *
 * The native form controls already submit as Shopify line item properties.
 * This element:
 *  - validates the neck measure (whole centimetres only, range from the
 *    size table) and computes the size shown as "Talla calculada",
 *  - combines that size with the chosen personalization (product option
 *    values) to select the real product variant from the product's own
 *    variant data (`script[data-collar-variants]`, rendered by Liquid), and
 *    writes its id into the form's `id` input,
 *  - updates every [data-collar-price] with that variant's price,
 *  - shows only the panel that applies ([data-collar-when="<option value>"]);
 *    inactive panels are disabled so their fields are neither required nor submitted,
 *  - shows only the color slots for the chosen number of colors,
 *  - redraws every [data-collar-preview] (reference only: Mundo Soma composes the real collar),
 *  - fills [data-summary] rows,
 *  - blocks the add to cart (before s-product-form handles it) while the
 *    design is incomplete or the variant is unavailable,
 *  - dispatches `collar:change` (bubbles) with the full design state.
 */
class SCollarConfigurator extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    this.touched = false;
    this.onChange = (event) => {
      // Neck measure: whole centimetres only, so drop anything that is not a digit as the buyer types.
      const target = event && event.target;
      if (target && target.matches && target.matches('[data-collar-input="measure"]')) {
        const digits = target.value.replace(/\D/g, '').slice(0, 3);
        if (digits !== target.value) target.value = digits;
      }
      this.update();
    };
    this.onBlur = (event) => {
      if (event.target.matches('[data-collar-input="measure"]') && event.target.value.trim()) {
        this.touched = true;
        this.update();
        this.maybeShowMeasureDialog();
      }
    };
    this.onInvalid = (event) => {
      if (event.target.matches('[data-collar-input="measure"]')) {
        this.touched = true;
        this.renderMeasureError(this.getMeasure());
        this.maybeShowMeasureDialog(true);
      }
    };
    this.onDialogClick = (event) => {
      const dialog = this.querySelector('[data-collar-measure-dialog]');
      if (!dialog || !dialog.contains(event.target)) return;
      if (event.target === dialog) {
        // Only clicks on the backdrop (outside the box) close it.
        const rect = dialog.getBoundingClientRect();
        const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) this.closeMeasureDialog(true);
        return;
      }
      if (event.target.closest('[data-collar-dialog-back]')) {
        event.preventDefault();
        this.closeMeasureDialog(true);
      } else if (event.target.closest('[data-collar-dialog-go]')) {
        event.preventDefault();
        this.closeMeasureDialog(false);
        this.goToSpecialSize();
      }
    };
    this.addEventListener('click', this.onDialogClick);
    this.onSubmit = (event) => this.guardSubmit(event);
    this.addEventListener('input', this.onChange);
    this.addEventListener('change', this.onChange);
    this.addEventListener('focusout', this.onBlur);
    this.addEventListener('invalid', this.onInvalid, true);
    // Capture phase: runs before s-product-form's submit listener.
    this.addEventListener('submit', this.onSubmit, true);
    this.setupMeasure();
    this.restoreDesign();
    this.update();
  }

  // --- Design memory: the same design follows the buyer between the product page and the standalone page.

  get storageKey() {
    return `mundo-soma:collar:${this.dataset.productId || ''}`;
  }

  saveDesign() {
    const design = this.readDesign();
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(design));
    } catch {
      // Storage unavailable (private mode): the design simply is not remembered.
    }
    return design;
  }

  /** Link that reopens this exact design (preview included); submitted with the order. */
  designLink(design) {
    const base = this.dataset.designUrl || `${window.location.origin}${window.location.pathname}`;
    const url = new URL(base, window.location.origin);
    // Compact and readable: medida~personalización~nombre~amuleto~color amuleto~cantidad~color 1~…~color 4
    const amulet = design.amulet || {};
    const slots = [1, 2, 3, 4].map((slot) => (design.colors && design.colors[slot]) || '');
    const parts = [design.measure, design.personalization, design.name, amulet.design, amulet.color, design.count, ...slots];
    const clean = parts.map((part) => String(part || '').replace(/~/g, ' '));
    while (clean.length && !clean[clean.length - 1]) clean.pop();
    url.searchParams.set('diseno', clean.join('~'));
    return url.toString();
  }

  /** A design shared through ?diseno=… (the order's "Ver diseño" link) wins over the remembered one. */
  designFromUrl() {
    try {
      const raw = new URLSearchParams(window.location.search).get('diseno');
      if (!raw) return null;
      const [measure, personalization, name, amuletDesign, amuletColor, count, ...slots] = raw.split('~');
      const colors = {};
      slots.forEach((value, i) => {
        if (value) colors[i + 1] = value;
      });
      return {
        measure: measure || '',
        personalization: personalization || '',
        name: name || '',
        amulet: { design: amuletDesign || '', color: amuletColor || '' },
        count: count || '',
        colors,
      };
    } catch {
      return null;
    }
  }

  readDesign() {
    const measure = this.querySelector('[data-collar-input="measure"]');
    const name = this.querySelector('[data-collar-input="name"]');
    const radioValue = (selector) => {
      const input = this.querySelector(`${selector} input:checked`);
      return input ? input.value : '';
    };
    const colors = {};
    for (const slot of this.querySelectorAll('[data-collar-field="color"]')) {
      const input = slot.querySelector('input:checked');
      if (input) colors[slot.dataset.colorSlot] = input.value;
    }
    const design = {
      measure: measure ? measure.value : '',
      personalization: radioValue('[data-collar-field="personalization"]'),
      name: name ? name.value : '',
      amulet: {
        design: this.querySelector('[data-collar-amulet] [data-amulet-design]:checked')?.value || '',
        color: radioValue('[data-collar-amulet] [data-amulet-colors]:not([disabled])'),
      },
      count: radioValue('[data-collar-field="count"]'),
      colors,
    };
    return design;
  }

  restoreDesign() {
    let design = this.designFromUrl();
    if (!design) {
      try {
        design = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      } catch {
        design = null;
      }
    }
    if (!design || typeof design !== 'object') return;
    const check = (selector, value) => {
      if (!value) return;
      for (const input of this.querySelectorAll(`${selector} input[type="radio"]`)) {
        if (input.value === value) input.checked = true;
      }
    };
    const measure = this.querySelector('[data-collar-input="measure"]');
    if (measure && typeof design.measure === 'string') measure.value = design.measure;
    const name = this.querySelector('[data-collar-input="name"]');
    if (name && typeof design.name === 'string') name.value = design.name;
    check('[data-collar-field="personalization"]', design.personalization);
    const amulet = design.amulet && typeof design.amulet === 'object' ? design.amulet : null;
    if (amulet && /^[a-z0-9_-]+$/i.test(amulet.design || '')) {
      check('[data-collar-amulet] fieldset:first-of-type', amulet.design);
      check(`[data-amulet-colors="${amulet.design}"]`, amulet.color);
    }
    check('[data-collar-field="count"]', design.count);
    if (design.colors && typeof design.colors === 'object') {
      for (const slot of this.querySelectorAll('[data-collar-field="color"]')) {
        check(`[data-collar-field="color"][data-color-slot="${slot.dataset.colorSlot}"]`, design.colors[slot.dataset.colorSlot]);
      }
    }
    if (design.measure) this.touched = true;
  }

  // --- Reference drawing: Mundo Soma composes the real collar, the preview only shows the chosen colors and details.

  /** SVG contents for [data-collar-art]: closure, cord, terminals, focal pieces, side beads and name or amulet. */
  artMarkup(options) {
    const colors = options.colors && options.colors.length ? options.colors : ['#CFC3E8'];
    const letters = Array.from(options.name || '').slice(0, 20);
    const charm = Boolean(options.charm) && !letters.length;
    const CX = 200, CY = 200, R = 140, DARK = '#1f1d1b';
    const round = (n) => Math.round(n * 10) / 10;
    const pt = (deg) => {
      const rad = (deg * Math.PI) / 180;
      return [round(CX + R * Math.sin(rad)), round(CY - R * Math.cos(rad))];
    };
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const color = (i) => colors[i % colors.length];
    const out = [];
  
    // Black cord, closure ring and black terminals at the top.
    const [sx, sy] = pt(9);
    const [ex, ey] = pt(351);
    out.push(`<path data-layer="cord" d="M${sx} ${sy} A${R} ${R} 0 1 1 ${ex} ${ey}" fill="none" stroke="${DARK}" stroke-width="3"/>`);
    out.push(`<g data-layer="ring"><path d="M${sx} ${sy} L${CX + 7} ${CY - R - 12} M${ex} ${ey} L${CX - 7} ${CY - R - 12}" stroke="${DARK}" stroke-width="3" stroke-linecap="round"/><circle cx="${CX}" cy="${CY - R - 22}" r="11" fill="none" stroke="${DARK}" stroke-width="4.5"/></g>`);
    out.push('<g data-layer="terminals">');
    for (const deg of [9, -9]) {
      const [x, y] = pt(deg);
      out.push(`<rect x="${round(x - 9)}" y="${round(y - 6)}" width="18" height="12" rx="3" fill="${DARK}" transform="rotate(${deg} ${x} ${y})"/>`);
    }
    out.push('</g>');
  
    // Geometric focal pieces near the closure.
    out.push('<g data-layer="focal">');
    for (const side of [1, -1]) {
      const [hx, hy] = pt(side * 21);
      const hex = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        return `${round(hx + 13 * Math.cos(a))},${round(hy + 13 * Math.sin(a))}`;
      }).join(' ');
      out.push(`<polygon class="collar-focal" points="${hex}" fill="${color(1)}"/>`);
      const [dx, dy] = pt(side * 33);
      out.push(`<rect class="collar-focal" x="${round(dx - 9)}" y="${round(dy - 9)}" width="18" height="18" rx="2" fill="${color(0)}" transform="rotate(${45 + side * 33} ${dx} ${dy})"/>`);
    }
    out.push('</g>');
  
    // Bottom centre: letter beads (white bead, one black letter each) with separators, or the amulet.
    const step = 9.6;
    const half = letters.length ? ((letters.length - 1) / 2) * step : 0;
    const separator = (deg) => {
      const [x, y] = pt(deg);
      return `<ellipse cx="${x}" cy="${y}" rx="3.5" ry="10" fill="${DARK}" transform="rotate(${deg} ${x} ${y})"/>`;
    };
    let sideEnd = 174.4;
    if (letters.length) {
      out.push('<g data-layer="letters">');
      letters.forEach((letter, i) => {
        // Left to right as typed: angles past 180° sit on the left of the drawing, so the first letter takes the largest angle.
      const [x, y] = pt(180 - (i - (letters.length - 1) / 2) * step);
        out.push(`<circle cx="${x}" cy="${y}" r="11" fill="#fff" stroke="${DARK}" stroke-opacity=".3" stroke-width="1.5"/><text x="${x}" y="${y}" dy=".35em" text-anchor="middle" class="collar-letter">${esc(letter)}</text>`);
      });
      out.push(separator(180 - half - 8.5), separator(180 + half + 8.5), '</g>');
      sideEnd = 180 - half - 17.5;
    } else if (charm && typeof options.charm === 'object') {
      // The merchant's amulet icon, painted in the chosen color (line art stays black); hangs from the bottom bead.
      const [x, y] = pt(180);
      const art = this.amuletArt(options.charm.image, options.charm.hex);
      let icon = '';
      if (art) {
        const box = 52;
        const iw = art.w >= art.h ? box : (box * art.w) / art.h;
        const ih = art.h >= art.w ? box : (box * art.h) / art.w;
        icon = `<image href="${esc(art.src)}" x="${round(x - iw / 2)}" y="${y + 14}" width="${round(iw)}" height="${round(ih)}" preserveAspectRatio="xMidYMin meet"/>`;
      }
      out.push(`<g data-layer="charm">${separator(180)}<line x1="${x}" y1="${y + 8}" x2="${x}" y2="${y + 16}" stroke="${DARK}" stroke-width="2.5"/>${icon}</g>`);
      sideEnd = 171;
    } else if (charm) {
      const [x, y] = pt(180);
      out.push(`<g data-layer="charm">${separator(180)}<line x1="${x}" y1="${y + 8}" x2="${x}" y2="${y + 22}" stroke="${DARK}" stroke-width="2.5"/><circle cx="${x}" cy="${y + 25}" r="4" fill="none" stroke="${DARK}" stroke-width="2"/><circle class="collar-charm" cx="${x}" cy="${y + 44}" r="16"/><path d="M${x} ${y + 51} c-9 -6 -10 -11 -7 -14 c2.5 -2.5 6 -1 7 1.5 c1 -2.5 4.5 -4 7 -1.5 c3 3 2 8 -7 14z" fill="#fff" fill-opacity=".9"/></g>`);
      sideEnd = 171;
    }
  
    // Round beads along both sides, mirrored so the collar stays balanced.
    out.push('<g data-layer="beads">');
    const start = 44;
    const count = Math.max(1, Math.round((sideEnd - start) / 11.2) + 1);
    for (let j = 0; j < count; j += 1) {
      const deg = count === 1 ? start : start + (j * (sideEnd - start)) / (count - 1);
      for (const side of [1, -1]) {
        const [x, y] = pt(side * deg);
        out.push(`<circle class="collar-bead" cx="${x}" cy="${y}" r="12" fill="${color(j)}"/>`);
      }
    }
    out.push('</g>');
    return out.join('');
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.onChange);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('focusout', this.onBlur);
    this.removeEventListener('invalid', this.onInvalid, true);
    this.removeEventListener('submit', this.onSubmit, true);
  }

  readVariants() {
    const script = this.querySelector('script[data-collar-variants]');
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  // The visible input keeps validation; the submitted property moves to a
  // hidden field so the order shows the normalized value ("38,5 cm").
  setupMeasure() {
    const input = this.querySelector('[data-collar-input="measure"]');
    const output = this.querySelector('[data-collar-measure-output]');
    if (!input || !output || !input.name) return;
    output.name = input.name;
    input.removeAttribute('name');
  }

  parseRanges(field) {
    const ranges = [];
    for (const line of String(field.dataset.sizeRanges || '').split(';')) {
      const parts = line.split('|').map((part) => part.trim());
      if (parts.length < 3) continue;
      const from = Number.parseFloat(parts[1].replace(',', '.'));
      const to = Number.parseFloat(parts[2].replace(',', '.'));
      if (parts[0] && Number.isFinite(from) && Number.isFinite(to)) ranges.push({ size: parts[0], from, to });
    }
    return ranges;
  }

  /** Reads the measure field: { raw, value, size, formatted, error }. */
  getMeasure() {
    const field = this.querySelector('[data-collar-field="measure"]');
    const input = field ? field.querySelector('[data-collar-input="measure"]') : null;
    const empty = { raw: '', value: null, size: '', formatted: '', error: '', field, input };
    if (!field || !input) return empty;

    const raw = input.value.trim();
    if (!raw) return { ...empty, error: field.dataset.errorRequired || '' };
    if (!/^\d{1,3}$/.test(raw)) return { ...empty, raw, error: field.dataset.errorFormat || '' };

    const value = Number.parseFloat(raw.replace(',', '.'));
    const ranges = this.parseRanges(field);
    const min = ranges.length ? ranges[0].from : Number.parseFloat(input.dataset.min);
    const max = ranges.length ? ranges[ranges.length - 1].to : Number.parseFloat(input.dataset.max);
    if (!Number.isFinite(value) || value < min || value > max) {
      return { ...empty, raw, error: field.dataset.errorRange || '' };
    }

    // First size includes its lower bound; every size includes its upper bound.
    const match = ranges.find((range, index) => (index === 0 ? value >= range.from : value > range.from) && value <= range.to);
    const formatted = `${String(Math.round(value * 100) / 100).replace('.', ',')} ${field.dataset.unit || 'cm'}`;
    return { ...empty, raw, value, size: match ? match.size : '', formatted };
  }

  /** Out-of-range measure dialog: leads to the "¿Necesitas otra medida?" card. */
  maybeShowMeasureDialog(force = false) {
    if (window.Shopify && window.Shopify.designMode) return;
    const measure = this.getMeasure();
    const outOfRange = Boolean(measure.raw) && /^\d{1,3}$/.test(measure.raw) && Boolean(measure.error);
    if (!outOfRange) {
      this.dialogShownFor = '';
      return;
    }
    if (!force && this.dialogShownFor === measure.raw) return;
    const dialog = this.querySelector('[data-collar-measure-dialog]');
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
    this.dialogShownFor = measure.raw;
    dialog.showModal();
  }

  closeMeasureDialog(focusInput) {
    const dialog = this.querySelector('[data-collar-measure-dialog]');
    if (dialog && dialog.open) dialog.close();
    if (focusInput) {
      const input = this.querySelector('[data-collar-input="measure"]');
      if (input) {
        input.focus({ preventScroll: false });
        input.select();
      }
    }
  }

  goToSpecialSize() {
    const card = this.querySelector('[data-collar-special-size]') || document.querySelector('[data-collar-special-size]');
    if (!card) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    card.focus({ preventScroll: true });
    card.classList.add('is-highlighted');
    clearTimeout(this.highlightTimer);
    this.highlightTimer = setTimeout(() => card.classList.remove('is-highlighted'), 2400);
  }

  renderMeasureError(measure) {
    if (!measure.input) return;
    measure.input.setCustomValidity(measure.error);
    const show = Boolean(measure.error) && this.touched;
    measure.input.toggleAttribute('aria-invalid', show);
    if (show) measure.input.setAttribute('aria-invalid', 'true');
    const error = this.querySelector('[data-collar-measure-error]');
    if (error) {
      error.textContent = show ? measure.error : '';
      error.hidden = !show;
    }
  }

  checkedIn(selector) {
    const field = this.querySelector(selector);
    return field ? field.querySelector('input:checked:not(:disabled)') : null;
  }

  labelOf(input) {
    return input ? input.dataset.label || input.value : '';
  }

  colorCount() {
    const parsed = Number.parseInt(this.labelOf(this.checkedIn('[data-collar-field="count"]')), 10);
    return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 4) : 1;
  }

  findVariant(size, personalization) {
    if (!size || !personalization) return null;
    return this.variants.find((variant) => variant.options.includes(size) && variant.options.includes(personalization)) || null;
  }

  getState() {
    const measure = this.getMeasure();
    const personalization = this.labelOf(this.checkedIn('[data-collar-field="personalization"]'));
    const count = this.colorCount();

    const colors = [];
    for (const slot of this.querySelectorAll('[data-collar-field="color"]')) {
      const index = Number.parseInt(slot.dataset.colorSlot, 10);
      if (index > count) continue;
      const input = slot.querySelector('input:checked');
      if (input) colors.push({ slot: index, name: this.labelOf(input), hex: input.dataset.color || '' });
    }

    const nameInput = this.querySelector('[data-collar-input="name"]');
    const nameActive = Boolean(nameInput && !nameInput.disabled);
    const amulet = this.getAmulet();
    const variant = this.findVariant(measure.size, personalization);

    return {
      measure: measure.formatted,
      measureError: measure.error,
      size: measure.size,
      personalization,
      variantId: variant ? String(variant.id) : null,
      available: variant ? Boolean(variant.available) : false,
      price: variant ? variant.price : null,
      compareAt: variant && variant.compareAt ? variant.compareAt : null,
      savings: variant && variant.savings ? variant.savings : null,
      name: nameActive ? nameInput.value.trim() : '',
      colorCount: count,
      colors,
      composition: this.dataset.composition || '',
      charm: amulet.label,
      hasCharm: Boolean(amulet.design),
      amulet,
    };
  }

  // --- Amulet (Con amuleto): design cards, colors of the chosen design, "Flor morada" property and preview art.

  /** Reads the amulet picker: chosen design, its color and the submitted label ("Flor morada"). */
  getAmulet() {
    const root = this.querySelector('[data-collar-amulet]');
    const empty = { active: false, design: '', designLabel: '', color: '', hex: '', image: '', label: '' };
    if (!root) return empty;
    const first = root.querySelector('[data-amulet-design]');
    const active = Boolean(first && !first.disabled);
    const designInput = root.querySelector('[data-amulet-design]:checked');
    if (!active || !designInput) return { ...empty, active };
    const group = root.querySelector(`[data-amulet-colors="${designInput.value}"]`);
    const colorInput = group ? group.querySelector('input:checked:not(:disabled)') : null;
    const designLabel = this.labelOf(designInput);
    const colorName = this.labelOf(colorInput);
    let label = '';
    if (colorName) {
      let adjective = colorName.toLocaleLowerCase('es');
      // "Flor" is feminine: Morado -> morada, Amarillo -> amarilla, Naranjo -> naranja (Rosa, Azul, Verde stay).
      if (designInput.dataset.feminine === 'true' && !adjective.includes(' ') && adjective.endsWith('o')) {
        adjective = `${adjective.slice(0, -1)}a`;
      }
      label = `${designLabel} ${adjective}`;
    }
    return {
      active,
      design: designInput.value,
      designLabel,
      color: colorName,
      hex: colorInput ? colorInput.dataset.color || '' : '',
      image: designInput.dataset.image || '',
      label,
    };
  }

  /** Shows only the colors of the chosen design, clears an incompatible color and fills the property + "Tu elección". */
  updateAmulet() {
    const root = this.querySelector('[data-collar-amulet]');
    if (!root) return;
    const first = root.querySelector('[data-amulet-design]');
    const active = Boolean(first && !first.disabled);
    const designInput = active ? root.querySelector('[data-amulet-design]:checked') : null;
    const chosen = designInput ? designInput.value : '';
    for (const group of root.querySelectorAll('[data-amulet-colors]')) {
      const on = active && group.dataset.amuletColors === chosen;
      group.hidden = !on;
      group.disabled = !on;
      for (const input of group.querySelectorAll('input')) {
        input.disabled = !on;
        if (!on && active) input.checked = false;
      }
    }
    const amulet = this.getAmulet();
    const value = root.querySelector('[data-collar-amulet-value]');
    if (value) value.value = amulet.label;
    const choice = root.querySelector('[data-amulet-choice]');
    if (choice) choice.hidden = !amulet.label;
    const text = root.querySelector('[data-amulet-choice-text]');
    if (text) text.textContent = amulet.label;
  }

  /** Cached painted icon for the preview; paints it in the background and redraws when ready. */
  amuletArt(src, hex) {
    if (!src) return null;
    if (!this.amuletCache) this.amuletCache = new Map();
    const key = `${src}|${hex || ''}`;
    if (this.amuletCache.has(key)) return this.amuletCache.get(key);
    this.amuletCache.set(key, null);
    this.paintAmulet(src, hex)
      .catch(() => ({ src, w: 1, h: 1 }))
      .then((art) => {
        this.amuletCache.set(key, art);
        if (this.isConnected) this.update();
      });
    return null;
  }

  /**
   * Paints the inside of the line icon: black strokes stay black, enclosed areas take the color
   * (white before a color is chosen), the outside stays transparent; then trims empty margins.
   */
  async paintAmulet(src, hex) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await img.decode();
    const scale = 320 / Math.max(img.naturalWidth, img.naturalHeight);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const image = ctx.getImageData(0, 0, w, h);
    const px = image.data;
    const total = w * h;
    const lum = (o) => (px[o] * 299 + px[o + 1] * 587 + px[o + 2] * 114) / 1000;

    const stroke = new Uint8Array(total);
    for (let i = 0; i < total; i += 1) stroke[i] = px[i * 4 + 3] >= 110 && lum(i * 4) < 150 ? 1 : 0;

    // Everything reachable from the border without crossing a stroke is outside the icon.
    const outside = new Uint8Array(total);
    const stack = [];
    const push = (i) => {
      if (!outside[i] && !stroke[i]) {
        outside[i] = 1;
        stack.push(i);
      }
    };
    for (let x = 0; x < w; x += 1) {
      push(x);
      push((h - 1) * w + x);
    }
    for (let y = 0; y < h; y += 1) {
      push(y * w);
      push(y * w + w - 1);
    }
    while (stack.length) {
      const i = stack.pop();
      const x = i % w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (i >= w) push(i - w);
      if (i < total - w) push(i + w);
    }

    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '');
    let fill = [255, 255, 255];
    if (match) {
      const value = match[1].length === 3 ? match[1].replace(/./g, '$&$&') : match[1];
      fill = [0, 2, 4].map((start) => Number.parseInt(value.slice(start, start + 2), 16));
    }

    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let i = 0; i < total; i += 1) {
      const o = i * 4;
      if (outside[i]) {
        // Soft edge pixels of the outline stay dark; any background turns transparent.
        if (px[o + 3] > 0 && lum(o) < 200) {
          px[o] = 31; px[o + 1] = 29; px[o + 2] = 27;
        } else {
          px[o + 3] = 0;
        }
        continue;
      }
      if (stroke[i]) {
        px[o] = 31; px[o + 1] = 29; px[o + 2] = 27;
      } else {
        px[o] = fill[0]; px[o + 1] = fill[1]; px[o + 2] = fill[2]; px[o + 3] = 255;
      }
      const x = i % w;
      const y = (i - x) / w;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    ctx.putImageData(image, 0, 0);
    if (maxX < 0) return { src: canvas.toDataURL('image/png'), w, h };

    const pad = 3;
    const cx = Math.max(0, minX - pad);
    const cy = Math.max(0, minY - pad);
    const cw = Math.min(w, maxX + pad + 1) - cx;
    const ch = Math.min(h, maxY + pad + 1) - cy;
    const crop = document.createElement('canvas');
    crop.width = cw;
    crop.height = ch;
    crop.getContext('2d').drawImage(canvas, cx, cy, cw, ch, 0, 0, cw, ch);
    return { src: crop.toDataURL('image/png'), w: cw, h: ch };
  }

  /** Shows only the panels whose [data-collar-when] matches the chosen personalization. */
  updateConditionalPanels() {
    const personalization = this.labelOf(this.checkedIn('[data-collar-field="personalization"]'));
    for (const panel of this.querySelectorAll('[data-collar-when]')) {
      const active = panel.dataset.collarWhen === personalization;
      panel.hidden = !active;
      for (const input of panel.querySelectorAll('input, select, textarea')) input.disabled = !active;
    }
  }

  /**
   * No repeated colors across the active slots (set data-allow-repeat on the
   * element to allow them later). A color taken by an earlier slot is disabled
   * in later slots; if a later slot holds a taken color it moves to the first free one.
   */
  enforceUniqueColors() {
    if (this.hasAttribute('data-allow-repeat')) return;
    const count = this.colorCount();
    const slots = [...this.querySelectorAll('[data-collar-field="color"]')].sort(
      (a, b) => Number(a.dataset.colorSlot) - Number(b.dataset.colorSlot),
    );
    const taken = new Set();
    for (const slot of slots) {
      const index = Number.parseInt(slot.dataset.colorSlot, 10);
      const inputs = [...slot.querySelectorAll('input[type="radio"]')];
      const active = index <= count;
      for (const input of inputs) input.disabled = active ? taken.has(input.value) : false;
      if (!active) continue;
      let checked = inputs.find((input) => input.checked);
      if (!checked || checked.disabled) {
        checked = inputs.find((input) => !input.disabled);
        if (checked) checked.checked = true;
      }
      if (checked) taken.add(checked.value);
    }
  }

  update() {
    this.updateConditionalPanels();
    this.updateAmulet();
    this.enforceUniqueColors();
    const state = this.getState();

    // Color slots: show up to the chosen count; disable hidden ones so they are not submitted.
    for (const slot of this.querySelectorAll('[data-collar-field="color"]')) {
      const index = Number.parseInt(slot.dataset.colorSlot, 10);
      const active = index <= state.colorCount;
      slot.hidden = !active;
      slot.disabled = !active;
      const selected = slot.querySelector('[data-swatch-selected]');
      const input = slot.querySelector('input:checked');
      if (selected) selected.textContent = input ? this.labelOf(input) : '';
    }

    // Steps that only apply from N colors (e.g. combination): dim and stop submitting below N.
    for (const step of this.querySelectorAll('[data-collar-min-colors]')) {
      const active = state.colorCount >= Number.parseInt(step.dataset.collarMinColors, 10);
      step.toggleAttribute('data-inactive', !active);
      for (const input of step.querySelectorAll('input, select, textarea')) input.disabled = !active;
    }

    // Measure: validity, error message, computed size and the submitted "<value> cm".
    this.renderMeasureError(this.getMeasure());
    const sizeOutput = this.querySelector('[data-collar-size-output]');
    if (sizeOutput) sizeOutput.textContent = state.size || sizeOutput.dataset.empty || '';
    const measureOutput = this.querySelector('[data-collar-measure-output]');
    if (measureOutput) measureOutput.value = state.measure;

    // Real variant: size from the measure + personalization.
    const variantInput = this.querySelector('[data-collar-variant-input]');
    if (variantInput) variantInput.value = state.variantId && state.available ? state.variantId : '';

    // Price follows the selected variant; until then show the "desde" price.
    for (const price of this.querySelectorAll('[data-collar-price]')) {
      price.textContent = state.price || price.dataset.fromPrice || '';
    }
    for (const note of this.querySelectorAll('[data-collar-price-note]')) note.hidden = Boolean(state.price);
    // Regular price (variant compare-at) struck through and the real saving, only when the variant has one.
    for (const compare of this.querySelectorAll('[data-collar-compare]')) {
      const amount = compare.querySelector('[data-collar-compare-amount]');
      if (amount) amount.textContent = state.compareAt || '';
      compare.hidden = !state.compareAt;
    }
    for (const savings of this.querySelectorAll('[data-collar-savings]')) {
      savings.textContent = state.savings ? (savings.dataset.template || '__AMOUNT__').replace('__AMOUNT__', state.savings) : '';
      savings.hidden = !state.savings;
    }

    // Status line above the add to cart button.
    if (state.variantId && !state.available) this.showStatus(this.dataset.unavailable);
    else if (this.statusFromSubmit && !this.isComplete(state)) this.showStatus(this.dataset.incomplete);
    else this.showStatus('');

    // Preview: a reference drawing. Mundo Soma composes the real collar, so the order never stores a bead layout.
    const hexes = state.colors.map((color) => color.hex).filter(Boolean);
    const nameActive = state.personalization && Boolean(this.querySelector('[data-collar-input="name"]:not(:disabled)'));
    const art = this.artMarkup({
      colors: hexes,
      // Letter beads are uppercase; only the preview is transformed, the submitted property keeps what was typed.
      name: nameActive ? state.name.toLocaleUpperCase('es') : '',
      charm: state.hasCharm ? state.amulet : null,
    });
    for (const preview of this.querySelectorAll('[data-collar-preview]')) {
      hexes.forEach((hex, i) => preview.style.setProperty(`--collar-color-${i + 1}`, hex));
      preview.dataset.colorCount = String(state.colorCount);
      preview.dataset.charm = state.hasCharm ? state.charm : '';
      const group = preview.querySelector('[data-collar-art]');
      if (group) group.innerHTML = art;
    }

    // Name counter.
    const counter = this.querySelector('[data-collar-name-count]');
    if (counter) counter.textContent = String(state.name.length);

    // Match (only when the Match block is on the page).
    const matchInput = this.querySelector('[data-collar-input="match"]:not(:disabled)');
    if (matchInput) {
      const matchOff = matchInput.closest('[data-collar-field="match"]')?.querySelector('input[type="hidden"]');
      state.match = matchInput.checked ? matchInput.value : matchOff ? matchOff.value : '';
    } else {
      state.match = '';
    }

    // Summary rows; optional rows only show when they apply.
    const values = {
      measure: state.measure,
      size: state.size,
      personalization: state.personalization,
      name: state.name,
      charm: state.charm,
      colors: state.colors.map((color) => color.name).join(', '),
      composition: state.composition,
      match: state.match,
    };
    const applies = {
      name: Boolean(this.querySelector('[data-collar-input="name"]:not(:disabled)')),
      charm: Boolean(this.querySelector('[data-collar-field="charm"] input:not(:disabled)')),
      match: Boolean(matchInput),
    };
    for (const cell of this.querySelectorAll('[data-summary]')) {
      const key = cell.dataset.summary;
      cell.textContent = values[key] || '—';
      const row = cell.closest('[data-summary-optional]');
      if (row) row.hidden = !applies[key];
    }

    const design = this.saveDesign();
    const designInput = this.querySelector('[data-collar-design-link]');
    if (designInput) designInput.value = state.variantId ? this.designLink(design) : '';
    this.dispatchEvent(new CustomEvent('collar:change', { bubbles: true, detail: state }));
  }

  isComplete(state) {
    const form = this.querySelector('form');
    return Boolean(state.variantId && state.available && (!form || form.checkValidity()));
  }

  showStatus(message) {
    const status = this.querySelector('[data-collar-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  }

  guardSubmit(event) {
    this.touched = true;
    this.update();
    const state = this.getState();
    if (this.isComplete(state)) {
      this.statusFromSubmit = false;
      this.showStatus('');
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.maybeShowMeasureDialog(true);
    this.statusFromSubmit = true;
    this.showStatus(state.variantId && !state.available ? this.dataset.unavailable : this.dataset.incomplete);
    const form = event.target instanceof HTMLFormElement ? event.target : this.querySelector('form');
    if (form) form.reportValidity();
  }
}

if (!customElements.get('s-collar-configurator')) customElements.define('s-collar-configurator', SCollarConfigurator);
